import React from 'react';
import toast from '../../lib/toast';
import { patientsService } from '../../adapters/patients';
import { Patient } from '../../types';

interface CreatePatientModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (patient: Patient) => void;
}

interface PatientForm {
  first_name: string;
  last_name: string;
  sex_at_birth: string;
  date_of_birth: string;
  email: string;
  phone: string;
}

const CreatePatientModal = ({ open, onClose, onCreated }: CreatePatientModalProps) => {
  const [form, setForm] = React.useState<PatientForm>({
    first_name: '',
    last_name: '',
    sex_at_birth: 'unknown',
    date_of_birth: '',
    email: '',
    phone: '',
  });
  const [submitting, setSubmitting] = React.useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleAnonymous = async () => {
    const timestamp = new Date().toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
    setSubmitting(true);
    try {
      const created = await patientsService.createPatient({
        first_name: 'Anonymous',
        last_name: timestamp,
        sex_at_birth: 'unknown',
      });
      toast.success('Anonymous patient created');
      onCreated(created);
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.msg || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) {
      toast.error('First and last name required');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Partial<Patient> = { ...form };
      if (!payload.date_of_birth) delete payload.date_of_birth;
      if (!payload.email) delete payload.email;
      if (!payload.phone) delete payload.phone;
      const created = await patientsService.createPatient(payload);
      toast.success('Patient created');
      onCreated(created);
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.msg || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-[100] flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />
      <div className='relative bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden'>
        <div className='bg-brand-navy px-6 py-4'>
          <h2 className='text-xl font-semibold text-white'>New Patient</h2>
        </div>
        <form onSubmit={handleSubmit} className='p-6'>
          <div className='space-y-5'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1.5'>
                  First Name <span className='text-red-500'>*</span>
                </label>
                <input
                  name='first_name'
                  value={form.first_name}
                  onChange={handleChange}
                  placeholder='Enter first name'
                  required
                  className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1.5'>
                  Last Name <span className='text-red-500'>*</span>
                </label>
                <input
                  name='last_name'
                  value={form.last_name}
                  onChange={handleChange}
                  placeholder='Enter last name'
                  required
                  className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1.5'>Sex at Birth</label>
                <select
                  name='sex_at_birth'
                  value={form.sex_at_birth}
                  onChange={handleChange}
                  className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm'
                >
                  <option value='unknown'>Unknown</option>
                  <option value='male'>Male</option>
                  <option value='female'>Female</option>
                  <option value='intersex'>Intersex</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1.5'>Date of Birth</label>
                <input
                  type='date'
                  name='date_of_birth'
                  value={form.date_of_birth}
                  onChange={handleChange}
                  className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1.5'>Email</label>
                <input
                  type='email'
                  name='email'
                  value={form.email}
                  onChange={handleChange}
                  placeholder='email@example.com'
                  className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1.5'>Phone</label>
                <input
                  name='phone'
                  value={form.phone}
                  onChange={handleChange}
                  placeholder='(123) 456-7890'
                  className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm'
                />
              </div>
            </div>
          </div>
          <div className='flex justify-between items-center gap-3 mt-6 pt-4 border-t border-gray-200'>
            <button
              type='button'
              onClick={handleAnonymous}
              disabled={submitting}
              className='px-5 py-2.5 text-sm font-medium rounded-lg border border-brand-accent bg-white text-brand-accent hover:bg-brand-accent/10 focus:outline-none focus:ring-2 focus:ring-brand-accent/40 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Anonymous Patient
            </button>
            <div className='flex gap-3'>
              <button
                type='button'
                onClick={onClose}
                className='px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-navy/40'
              >
                Cancel
              </button>
              <button
                type='submit'
                disabled={submitting}
                className='px-5 py-2.5 text-sm font-medium rounded-lg bg-brand-navy text-white hover:bg-brand-navy/90 focus:outline-none focus:ring-2 focus:ring-brand-navy/40 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm'
              >
                {submitting ? 'Creating...' : 'Create Patient'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePatientModal;
